import { getRankValue } from "./card.js";
import { RANKS, SUITS } from "./constants.js";
import { PLAYER_ROLE_CPU, PLAYER_ROLE_HUMAN, resetTableSlots } from "./player.js";

export const CPU_DIFFICULTIES = Object.freeze(["normal", "hard"]);
const RANK_RESET = "2";
const RANK_CLEAR = "10";
const HARD_PRESERVE_STRONG_TOP_RANK_MAX = 4;
const HARD_PRESERVE_STRONG_NON_POWER_THRESHOLD = 8;
const HARD_PRESERVE_STRONG_DECK_THRESHOLD = 5;
const HARD_HIGH_NON_SPECIAL_RANKS = new Set(["9", "J", "Q", "K", "A"]);
const HARD_EMPTY_STACK_CHANCE_MIN_DECK_SIZE = 11;
const HARD_PRESERVE_CLEAR_HIGH_PILE_MIN_DECK_SIZE = 11;
const HARD_PRESERVE_CLEAR_HIGH_PILE_MAX_STACK_SIZE = 3;
const HARD_SMALL_HIGH_PILE_PICKUP_MIN_DECK_SIZE = 11;
const HARD_SMALL_HIGH_PILE_PICKUP_MAX_STACK_SIZE = 2;
const HARD_SKIP_HIGH_RANK_CHAIN_MIN = 9;
const HARD_OVERCOMMIT_DRAW_MAX_HAND_SIZE = 10;
const HARD_OVERCOMMIT_DRAW_MIN_DUPLICATES = 2;
const HARD_OVERCOMMIT_DRAW_MIN_OPPONENT_HAND = 0;
const HARD_OVERCOMMIT_DRAW_MIN_TOP_RANK = 13;
const HARD_OVERCOMMIT_DRAW_MAX_STACK_SIZE = 1;
const HARD_OVERCOMMIT_DRAW_MIN_DECK_SIZE = 6;
const HARD_OVERCOMMIT_DRAW_MAX_REMAINING_HIGH_DENSITY_PERCENT = 31;
const HARD_STEAL_WASTEPILE_MAX_STACK_SIZE = 6;
const HARD_STEAL_WASTEPILE_MIN_DECK_SIZE = 5;
const HARD_CLEAR_OVER_RESET_STACK_SIZE_MIN = 1;
const HARD_THIN_DECK_SIZE_MAX = 2;
const HARD_THIN_DECK_CLEAR_OVER_RESET_STACK_SIZE_MIN = 1;
const HARD_ZONE_EXIT_NEXT_ZONE_HIGH_RANK_MIN = 11;
const HARD_ENDGAME_MEMORY_PRESSURE_LOOP_LIMIT = 3;
const HARD_ENDGAME_VISIBLE_LOCK_LOOP_LIMIT = 3;
const HARD_ENDGAME_UNKNOWN_HAND_PRESSURE_LOOP_LIMIT = 3;
const HARD_ENDGAME_UNKNOWN_HAND_PRESSURE_MAX_OPPONENT_HAND = 1;
const HARD_ENDGAME_UNKNOWN_HAND_PRESSURE_MIN_NON_POWER_ACTIONS = 5;
const HARD_ENDGAME_UNKNOWN_HAND_PRESSURE_TOP_FRACTION = 0.4;
const HARD_ENDGAME_KNOWN_HAND_CONSERVE_LOOP_LIMIT = 3;
const HARD_ENDGAME_KNOWN_HAND_CONSERVE_RANK_MIN = 14;
const HARD_REASON_ENDGAME_MEMORY_PRESSURE = "hard:endgame-memory-pressure";
const HARD_REASON_ENDGAME_MEMORY_LOOP_FALLBACK = "hard:endgame-memory-loop-fallback";
const HARD_SETUP_PAIR_RANK_BONUS = 4;
const HARD_SETUP_TRIPLE_RANK_BONUS = 12;
const CPU_ACTION_CARD = Symbol("cpuActionCard");
const CPU_MEMORY_FAST_META = Symbol("cpuMemoryFastMeta");

export function normalizeCpuDifficulty(difficulty) {
  const normalized = typeof difficulty === "string" ? difficulty.trim().toLowerCase() : "";
  return CPU_DIFFICULTIES.includes(normalized) ? normalized : "normal";
}

function shouldTrackCpuMemory(cpuPlayer) {
  return Boolean(
    cpuPlayer
      && cpuPlayer.role === PLAYER_ROLE_CPU
      && normalizeCpuDifficulty(cpuPlayer.cpuDifficulty) === "hard"
  );
}

export function createCpuMemory() {
  return installCpuMemoryLazyAccessors({
    cards: {
      seenCards: [],
      remainingCards: []
    },
    opponent: {
      zones: {
        hand: createOpponentZoneMemory()
      }
    }
  });
}

export function refreshCpuMemoryForAll(state) {
  if (!state || !Array.isArray(state.players)) {
    return [];
  }

  const cpuPlayers = state.players.filter((player) => shouldTrackCpuMemory(player));
  if (cpuPlayers.length === 0) {
    return [];
  }

  const hasHumanOpponent = state.players.some((player) => player?.role === PLAYER_ROLE_HUMAN);
  if (!hasHumanOpponent) {
    return cpuPlayers.map((cpuPlayer) => markCpuMemoryPendingRefresh(cpuPlayer));
  }

  const publicFaceUpCards = getPublicFaceUpCards(state);
  return cpuPlayers.map((cpuPlayer) => refreshCpuMemoryInternal(state, cpuPlayer, publicFaceUpCards));
}

export function refreshCpuMemory(state, cpuPlayer) {
  const publicFaceUpCards = getPublicFaceUpCards(state);
  return refreshCpuMemoryInternal(state, cpuPlayer, publicFaceUpCards);
}

function refreshCpuMemoryInternal(state, cpuPlayer, publicFaceUpCards) {
  const memory = ensureCpuMemory(cpuPlayer);
  if (!state || !cpuPlayer) {
    return memory;
  }
  if (!shouldTrackCpuMemory(cpuPlayer)) {
    resetOpponentMemoryRaw(memory);
    return memory;
  }

  const opponent = getPrimaryMemoryOpponent(state, cpuPlayer);
  if (!opponent) {
    resetOpponentMemoryRaw(memory);
    return memory;
  }

  const fastMeta = ensureCpuMemoryFastMeta(memory);
  fastMeta.pendingRefresh = false;

  let seenCardsChanged = false;

  if (didCardIdSnapshotChange(fastMeta, "ownHandIdsSnapshot", cpuPlayer.hand)) {
    seenCardsChanged = rememberSeenCardRefs(memory, cpuPlayer.hand) || seenCardsChanged;
  }
  if (didCardIdSnapshotChange(fastMeta, "ownFaceUpIdsSnapshot", cpuPlayer.faceUp)) {
    seenCardsChanged = rememberSeenCardRefs(memory, cpuPlayer.faceUp) || seenCardsChanged;
  }
  if (didCardIdSnapshotChange(fastMeta, "publicFaceUpIdsSnapshot", publicFaceUpCards)) {
    seenCardsChanged = rememberSeenCardRefs(memory, publicFaceUpCards) || seenCardsChanged;
  }

  const playTopCard = Array.isArray(state.playStack) && state.playStack.length > 0
    ? state.playStack[0]
    : null;
  const playTopId = playTopCard?.id ?? null;
  if (playTopId !== fastMeta.playTopCardId) {
    fastMeta.playTopCardId = playTopId;
    if (playTopCard) {
      seenCardsChanged = rememberSeenCardRefs(memory, [playTopCard]) || seenCardsChanged;
    }
  }

  if (Array.isArray(state.discardStack)) {
    if (state.discardStack.length < fastMeta.observedDiscardCount) {
      fastMeta.observedDiscardCount = 0;
    }
    if (state.discardStack.length > fastMeta.observedDiscardCount) {
      seenCardsChanged = rememberSeenCardRefs(memory, state.discardStack.slice(fastMeta.observedDiscardCount)) || seenCardsChanged;
      fastMeta.observedDiscardCount = state.discardStack.length;
    }
  }

  const opponentMemoryKey = getPlayerMemoryKey(opponent);
  let shouldRefreshOpponentHandMemory = seenCardsChanged;
  if (opponentMemoryKey !== fastMeta.opponentMemoryKey) {
    fastMeta.opponentMemoryKey = opponentMemoryKey;
    fastMeta.opponentHandIdsSnapshot = null;
    shouldRefreshOpponentHandMemory = true;
  }
  if (didCardIdSnapshotChange(fastMeta, "opponentHandIdsSnapshot", opponent.hand)) {
    shouldRefreshOpponentHandMemory = true;
  }
  if (shouldRefreshOpponentHandMemory) {
    updateKnownOpponentHandCards(memory, opponent.hand, fastMeta.seenIds);
  }

  return memory;
}

export function rememberSeenCards(cpuPlayer, cards) {
  if (!shouldTrackCpuMemory(cpuPlayer)) {
    return [];
  }

  const memory = ensureCpuMemory(cpuPlayer);
  rememberSeenCardRefs(memory, cards);
  return [...memory.cards.seenCards];
}

export function rememberHumanWastepilePickup(cpuPlayer, cards) {
  rememberSeenCards(cpuPlayer, cards);
  return getCpuSeenHumanWastepileCardIds(cpuPlayer);
}

export function rememberHumanRearrangedCards(cpuPlayer, cards) {
  rememberSeenCards(cpuPlayer, cards);
  return getCpuSeenHumanRearrangedCardIds(cpuPlayer);
}

export function rememberDiscardedCards(cpuPlayer, cards) {
  rememberSeenCards(cpuPlayer, cards);
  return getCpuSeenDiscardedCardIds(cpuPlayer);
}

export function getCpuSeenHumanWastepileCardIds(cpuPlayer) {
  return getCpuSeenHumanRememberedCardIds(cpuPlayer);
}

export function getCpuSeenHumanRearrangedCardIds(cpuPlayer) {
  return getCpuSeenHumanRememberedCardIds(cpuPlayer);
}

export function getCpuSeenHumanRememberedCardIds(cpuPlayer) {
  if (!shouldTrackCpuMemory(cpuPlayer)) {
    return [];
  }
  return getCpuMemoryHandKnownCards(cpuPlayer).map((card) => card.id);
}

export function getCpuSeenDiscardedCardIds(cpuPlayer) {
  if (!shouldTrackCpuMemory(cpuPlayer)) {
    return [];
  }
  return getCpuMemoryCards(cpuPlayer, "seenCards").map((card) => card.id);
}

export function getCpuKnownHumanWastepileCards(state, cpuPlayer) {
  return getCpuKnownHumanRememberedCards(state, cpuPlayer);
}

export function getCpuKnownHumanRearrangedCards(state, cpuPlayer) {
  return getCpuKnownHumanRememberedCards(state, cpuPlayer);
}

export function getCpuKnownHumanRememberedCards(state, cpuPlayer) {
  if (!shouldTrackCpuMemory(cpuPlayer)) {
    return [];
  }
  return refreshCpuMemory(state, cpuPlayer).opponent.zones.hand.knownCards;
}

export function getCpuKnownDiscardedCards(state, cpuPlayer) {
  if (!shouldTrackCpuMemory(cpuPlayer)) {
    return [];
  }
  refreshCpuMemory(state, cpuPlayer);
  return Array.isArray(state?.discardStack) ? state.discardStack.map(createKnownCardRef) : [];
}

export function getCpuMemorySnapshot(cpuPlayer) {
  const memory = ensureCpuMemory(cpuPlayer);
  return {
    cards: {
      seenCards: cloneCardRefs(memory.cards.seenCards),
      remainingCards: cloneCardRefs(memory.cards.remainingCards)
    },
    opponent: {
      zones: {
        hand: cloneOpponentZoneSnapshot(memory.opponent.zones.hand)
      }
    }
  };
}

export function arrangeCpuSetupCards(player) {
  if (!player || !Array.isArray(player.hand) || !Array.isArray(player.faceUp)) {
    return false;
  }

  const faceUpCount = player.faceUp.length;
  const handCount = player.hand.length;
  const visibleCards = [...player.hand, ...player.faceUp].filter(Boolean);
  const faceUpSelectionCount = Math.min(faceUpCount, visibleCards.length);
  const difficulty = normalizeCpuDifficulty(player.cpuDifficulty);
  const faceUpCards = difficulty === "hard"
    ? chooseHardSetupFaceUpCards(visibleCards, faceUpSelectionCount)
    : chooseStrongestSetupFaceUpCards(visibleCards, faceUpSelectionCount);
  const faceUpIds = new Set(faceUpCards.map((card) => card.id));
  const handCards = visibleCards.filter((card) => !faceUpIds.has(card.id));

  player.faceUp = sortSetupCards(faceUpCards);
  player.hand = sortSetupCards(handCards).slice(0, handCount);
  resetTableSlots(player);
  return true;
}

export function chooseCpuAction({ state, player, actions, rng = Math.random }) {
  if (!player || !Array.isArray(actions) || actions.length === 0) {
    return null;
  }

  const difficulty = normalizeCpuDifficulty(player.cpuDifficulty);
  const context = buildContext(state, player, actions);

  if (difficulty === "hard") {
    return chooseHardAction(context, rng);
  }
  return chooseNormalAction(context, rng);
}

function buildContext(state, player, actions) {
  const topCard = state.playStack[0] ?? null;
  const topRankValue = topCard ? getRankValue(topCard) : 0;
  const topPressure = topRankValue >= 12 ? 2 : topRankValue >= 10 ? 1 : 0;
  const cpuMemory = ensureCpuMemory(player);
  const opponent = getPrimaryMemoryOpponent(state, player);
  const opponentHandCount = Array.isArray(opponent?.hand) ? opponent.hand.length : 0;

  const playActions = actions.filter((action) => action.type === "playCard");
  for (const action of playActions) {
    action[CPU_ACTION_CARD] = findActionCard(player, action);
  }
  const openActions = actions.filter((action) => action.type === "openFaceDownCard");
  const skipAction = actions.find((action) => action.type === "skipRemainingSameRank") ?? null;
  const drawAction = actions.find((action) => action.type === "drawChanceCard") ?? null;
  const pickUpAction = actions.find((action) => action.type === "pickUpWastepile") ?? null;

  return {
    state,
    player,
    opponent,
    topCard,
    topPressure,
    stackSize: state.playStack.length,
    deckSize: state.deck.length,
    handSize: player.hand.length,
    cpuMemory,
    opponentHandCount,
    playActions,
    openActions,
    skipAction,
    drawAction,
    pickUpAction
  };
}

function ensureCpuMemory(cpuPlayer) {
  if (!cpuPlayer) {
    return createCpuMemory();
  }

  const memory = cpuPlayer.cpuMemory;
  const structured = memory && typeof memory === "object" && memory.cards && memory.opponent
    ? memory
    : createCpuMemory();

  if (structured[CPU_MEMORY_FAST_META]) {
    cpuPlayer.cpuMemory = structured;
    return structured;
  }

  structured.cards = structured.cards && typeof structured.cards === "object" ? structured.cards : {};
  const seenCards = Array.isArray(structured.cards.seenCards)
    ? structured.cards.seenCards
    : Array.isArray(structured.cards.allSeenCards)
      ? structured.cards.allSeenCards
      : [];
  structured.cards.seenCards = seenCards.map(createKnownCardRef).filter(Boolean);
  delete structured.cards.allSeenCards;
  delete structured.cards.possibleRemainingCards;
  delete structured.cards.highestPossibleCardRemaining;
  delete structured.cards.highestPossibleNonSpecialCardRemaining;
  delete structured.cards.possibleSpecialCardsRemaining;
  structured.opponent = normalizeOpponentMemory(structured.opponent);

  cpuPlayer.cpuMemory = installCpuMemoryLazyAccessors(structured);
  return structured;
}

function createOpponentZoneMemory() {
  return {
    knownCards: []
  };
}

function normalizeOpponentMemory(opponentMemory) {
  const memory = createCpuMemory().opponent;
  if (!opponentMemory || typeof opponentMemory !== "object") {
    return memory;
  }

  const handZone = opponentMemory.zones?.hand;
  memory.zones.hand = {
    knownCards: Array.isArray(handZone?.knownCards) ? handZone.knownCards.map(createKnownCardRef).filter(Boolean) : []
  };
  return memory;
}

function installCpuMemoryLazyAccessors(memory) {
  memory.cards = memory.cards && typeof memory.cards === "object" ? memory.cards : {};
  memory.cards.seenCards = Array.isArray(memory.cards.seenCards) ? memory.cards.seenCards : [];
  defineLazyProperty(memory.cards, "remainingCards", () => getCpuMemoryRemainingCards(memory));

  memory.opponent = memory.opponent && typeof memory.opponent === "object" ? memory.opponent : {};
  const handZone = memory.opponent.zones?.hand && typeof memory.opponent.zones.hand === "object"
    ? memory.opponent.zones.hand
    : createOpponentZoneMemory();
  handZone.knownCards = Array.isArray(handZone.knownCards) ? handZone.knownCards : [];
  memory.opponent.zones = { hand: handZone };
  delete memory.opponent.currentZone;
  delete memory.opponent.facts;
  delete memory.opponent.statistics;

  ensureCpuMemoryFastMeta(memory);

  return memory;
}

function defineLazyProperty(target, fieldName, getter) {
  Object.defineProperty(target, fieldName, {
    enumerable: true,
    configurable: true,
    get: getter
  });
}

function ensureCpuMemoryFastMeta(memory) {
  if (!memory[CPU_MEMORY_FAST_META]) {
    const seenIds = new Set();
    const accountedRankSuitKeys = new Set();
    for (const card of memory.cards.seenCards) {
      if (!card?.id || !card.suit || !card.rank) {
        continue;
      }

      seenIds.add(card.id);
      accountedRankSuitKeys.add(`${card.suit}:${card.rank}`);
    }

    Object.defineProperty(memory, CPU_MEMORY_FAST_META, {
      enumerable: false,
      configurable: false,
      writable: false,
      value: {
        seenIds,
        accountedRankSuitKeys,
        seenVersion: 0,
        remainingCardsCache: null,
        observedDiscardCount: 0,
        ownHandIdsSnapshot: null,
        ownFaceUpIdsSnapshot: null,
        publicFaceUpIdsSnapshot: null,
        opponentHandIdsSnapshot: null,
        opponentMemoryKey: null,
        playTopCardId: null,
        hardEndgameLoopSignature: null,
        hardEndgameLoopCount: 0,
        hardEndgameVisibleLockSignature: null,
        hardEndgameVisibleLockCount: 0,
        hardEndgameUnknownHandPressureSignature: null,
        hardEndgameUnknownHandPressureCount: 0,
        hardEndgameKnownHandConserveSignature: null,
        hardEndgameKnownHandConserveCount: 0,
        pendingRefresh: false
      }
    });
  }

  return memory[CPU_MEMORY_FAST_META];
}

function getCpuMemoryRemainingCards(memory) {
  const meta = ensureCpuMemoryFastMeta(memory);
  if (Array.isArray(meta.remainingCardsCache)) {
    return meta.remainingCardsCache;
  }

  const candidates = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      if (!meta.accountedRankSuitKeys.has(`${suit}:${rank}`)) {
        candidates.push({ suit, rank });
      }
    }
  }

  meta.remainingCardsCache = candidates;
  return candidates;
}

function rememberSeenCardRefs(memory, cards) {
  if (!memory || !Array.isArray(cards)) {
    return false;
  }

  const target = memory.cards.seenCards;
  const meta = ensureCpuMemoryFastMeta(memory);
  let changed = false;
  for (const card of cards) {
    const cardId = card?.id;
    if (!cardId || meta.seenIds.has(cardId)) {
      continue;
    }

    const suit = card.suit;
    const rank = card.rank;
    if (!suit || !rank) {
      continue;
    }

    const cardRef = {
      id: cardId,
      suit,
      rank
    };

    target.push(cardRef);
    meta.seenIds.add(cardId);
    meta.accountedRankSuitKeys.add(`${cardRef.suit}:${cardRef.rank}`);
    changed = true;
  }

  if (changed) {
    meta.seenVersion += 1;
    meta.remainingCardsCache = null;
  }

  return changed;
}

function markCpuMemoryPendingRefresh(cpuPlayer) {
  const memory = ensureCpuMemory(cpuPlayer);
  ensureCpuMemoryFastMeta(memory).pendingRefresh = true;
  return memory;
}

function resetOpponentMemoryRaw(memory) {
  memory.opponent.zones.hand.knownCards = [];
}

function getPublicFaceUpCards(state) {
  if (!Array.isArray(state?.players)) {
    return [];
  }

  const cards = [];
  for (const player of state.players) {
    if (!Array.isArray(player?.faceUp)) {
      continue;
    }

    for (const card of player.faceUp) {
      cards.push(card);
    }
  }

  return cards;
}

function getPrimaryMemoryOpponent(state, cpuPlayer) {
  if (!Array.isArray(state?.players)) {
    return null;
  }

  const opponents = state.players.filter((player) => player && player !== cpuPlayer);
  if (opponents.length === 0) {
    return null;
  }

  return opponents.find((player) => player.role === PLAYER_ROLE_HUMAN) ?? opponents[0] ?? null;
}

function didCardIdSnapshotChange(meta, fieldName, cards) {
  const previousSnapshot = Array.isArray(meta[fieldName]) ? meta[fieldName] : null;
  const cardCount = Array.isArray(cards) ? cards.length : 0;

  if (previousSnapshot && previousSnapshot.length === cardCount) {
    for (let index = 0; index < cardCount; index += 1) {
      if ((cards[index]?.id ?? null) !== previousSnapshot[index]) {
        meta[fieldName] = copyCardIdSnapshot(cards);
        return true;
      }
    }

    return false;
  }

  meta[fieldName] = copyCardIdSnapshot(cards);
  return true;
}

function copyCardIdSnapshot(cards) {
  if (!Array.isArray(cards) || cards.length === 0) {
    return [];
  }

  const ids = [];
  for (const card of cards) {
    ids.push(card?.id ?? null);
  }
  return ids;
}

function updateKnownOpponentHandCards(memory, opponentHand, seenIds) {
  const knownCards = [];
  const sourceCards = Array.isArray(opponentHand) ? opponentHand : [];
  for (const card of sourceCards) {
    const cardId = card?.id;
    if (!cardId || !seenIds.has(cardId) || !card.suit || !card.rank) {
      continue;
    }

    knownCards.push({
      id: cardId,
      suit: card.suit,
      rank: card.rank
    });
  }

  if (areCardRefListsEqual(memory.opponent.zones.hand.knownCards, knownCards)) {
    return;
  }

  memory.opponent.zones.hand.knownCards = knownCards;
}

function areCardRefListsEqual(first, second) {
  if (!Array.isArray(first) || !Array.isArray(second) || first.length !== second.length) {
    return false;
  }

  for (let index = 0; index < first.length; index += 1) {
    if (first[index]?.id !== second[index]?.id) {
      return false;
    }
  }

  return true;
}

function getPlayerMemoryKey(player) {
  if (!player) {
    return null;
  }

  return `${player.role ?? "unknown"}:${player.name ?? "unknown"}`;
}


function createKnownCardRef(card) {
  if (!card?.id || !card.suit || !card.rank) {
    return null;
  }
  return {
    id: card.id,
    suit: card.suit,
    rank: card.rank
  };
}

function cloneOpponentZoneSnapshot(zone = createOpponentZoneMemory()) {
  return {
    knownCards: cloneCardRefs(zone.knownCards)
  };
}

function cloneCardRefs(cards) {
  return Array.isArray(cards) ? cards.map(cloneCardRef).filter(Boolean) : [];
}

function cloneCardRef(card) {
  return card ? { ...card } : null;
}

function getCpuMemoryHandKnownCards(cpuPlayer) {
  return ensureCpuMemory(cpuPlayer).opponent.zones.hand.knownCards;
}

function getCpuMemoryCards(cpuPlayer, fieldName) {
  const cards = ensureCpuMemory(cpuPlayer).cards[fieldName];
  return Array.isArray(cards) ? cards : [];
}

function chooseNormalAction(context, rng) {
  if (context.openActions.length > 0) {
    return pickRandom(context.openActions, rng);
  }

  const requiredChancePlay = chooseRequiredChancePlay(context);
  if (requiredChancePlay) {
    return requiredChancePlay;
  }

  if (context.playActions.length > 0) {
    if (context.skipAction && shouldSkipSameRank(context)) {
      return context.skipAction;
    }

    return choosePlayCardAction(context, rng);
  }

  if (context.skipAction) {
    return context.skipAction;
  }

  if (context.drawAction) {
    return context.drawAction;
  }

  if (context.pickUpAction) {
    return context.pickUpAction;
  }

  return null;
}

function chooseHardAction(context, rng) {
  if (context.openActions.length > 0) {
    return pickRandom(context.openActions, rng);
  }

  const requiredChancePlay = chooseRequiredChancePlay(context);
  if (requiredChancePlay) {
    return requiredChancePlay;
  }

  if (context.playActions.length > 0) {
    const endgamePowerFinishAction = chooseEndgamePowerFinishActionHard(context, rng);
    if (endgamePowerFinishAction) {
      return endgamePowerFinishAction;
    }

    const knownHandConserveDecision = chooseKnownHandEliteContinuationActionHard(context);
    if (knownHandConserveDecision?.forceNormalStrategy) {
      return chooseNormalAction(context, rng);
    }
    if (knownHandConserveDecision?.action) {
      return knownHandConserveDecision.action;
    }

    const endgameMemoryPressureDecision = chooseEndgameMemoryPressureActionHard(context, rng);
    if (endgameMemoryPressureDecision?.forceNormalStrategy) {
      return annotateHardActionReason(chooseNormalAction(context, rng), HARD_REASON_ENDGAME_MEMORY_LOOP_FALLBACK);
    }
    if (endgameMemoryPressureDecision?.action) {
      return annotateHardActionReason(endgameMemoryPressureDecision.action, HARD_REASON_ENDGAME_MEMORY_PRESSURE);
    }

    const endgameUnknownHandPressureAction = chooseEndgameUnknownHandPressureActionHard(context, rng);
    if (endgameUnknownHandPressureAction) {
      return endgameUnknownHandPressureAction;
    }

    const endgameVisibleLockDecision = chooseEndgameVisibleLockActionHard(context, rng);
    if (endgameVisibleLockDecision?.action) {
      return endgameVisibleLockDecision.action;
    }

    if (shouldPickUpHighPileToPreserveClearHard(context)) {
      return context.pickUpAction;
    }

    if (shouldStealWastepileHard(context)) {
      return context.pickUpAction;
    }

    if (shouldDrawChanceToPreserveStrongCardsHard(context)) {
      return context.drawAction;
    }

    if (shouldDrawChanceToAvoidHighEmptyStackOpenerHard(context)) {
      return context.drawAction;
    }

    if (shouldDrawChanceToAvoidHighRankChainOvercommitHard(context)) {
      return context.drawAction;
    }

    const clearOverResetAction = chooseClearOverResetActionHard(context, rng);
    if (clearOverResetAction) {
      return clearOverResetAction;
    }

    if (context.skipAction && (shouldSkipSameRank(context) || shouldSkipHighRankChainHard(context))) {
      return context.skipAction;
    }

    return choosePlayCardAction(context, rng);
  }

  if (context.skipAction) {
    return context.skipAction;
  }

  if (context.drawAction && context.pickUpAction) {
    return shouldDrawChanceHard(context) ? context.drawAction : context.pickUpAction;
  }

  if (context.drawAction) {
    return context.drawAction;
  }

  if (context.pickUpAction) {
    return context.pickUpAction;
  }

  return null;
}

function chooseEndgamePowerFinishActionHard(context, rng) {
  if (
    context.deckSize !== 0
    || context.player.hand.length < 2
    || context.player.faceUp.length !== 0
    || context.player.faceDown.length !== 0
  ) {
    return null;
  }

  const powerActions = context.playActions.filter((action) => isPowerCard(getCardForAction(context, action)));
  if (powerActions.length === 0) {
    return null;
  }

  if (!hasSingleRemainingNonPowerRank(context.player.hand)) {
    return null;
  }

  return choosePreferredEndgamePowerActionHard(context, powerActions, rng);
}

function hasSingleRemainingNonPowerRank(cards) {
  let rank = null;
  for (const card of cards) {
    if (isPowerCard(card)) {
      continue;
    }
    if (rank === null) {
      rank = card.rank;
      continue;
    }
    if (card.rank !== rank) {
      return false;
    }
  }

  return true;
}

function choosePreferredEndgamePowerActionHard(context, powerActions, rng) {
  const clearActions = powerActions.filter((action) => {
    const card = getCardForAction(context, action);
    return Boolean(card && card.rank === RANK_CLEAR);
  });
  const resetActions = powerActions.filter((action) => {
    const card = getCardForAction(context, action);
    return Boolean(card && card.rank === RANK_RESET);
  });

  if (context.stackSize > 0 && clearActions.length > 0 && resetActions.length > 0) {
    return pickLowestRankAction(context, clearActions, rng);
  }

  return pickLowestRankAction(context, powerActions, rng);
}

function chooseRequiredChancePlay(context) {
  if (!context.state.lastChanceCard) {
    return null;
  }

  return context.playActions.find((action) => action.cardId === context.state.lastChanceCard.id) ?? null;
}

function shouldStealWastepileHard(context) {
  if (!context.pickUpAction) {
    return false;
  }
  if (context.stackSize === 0 || context.stackSize > HARD_STEAL_WASTEPILE_MAX_STACK_SIZE) {
    return false;
  }
  if (context.deckSize < HARD_STEAL_WASTEPILE_MIN_DECK_SIZE) {
    return false;
  }
  if (context.player.hand.length === 0) {
    return false;
  }
  if (context.playActions.length > 1) {
    return false;
  }

  return context.state.playStack.every((card) => isHighOrSpecialCard(card));
}

function shouldPickUpHighPileToPreserveClearHard(context) {
  if (!context.pickUpAction || context.deckSize < HARD_PRESERVE_CLEAR_HIGH_PILE_MIN_DECK_SIZE) {
    return false;
  }
  if (context.stackSize < 1 || context.stackSize > HARD_PRESERVE_CLEAR_HIGH_PILE_MAX_STACK_SIZE) {
    return false;
  }
  if (!context.topCard || !HARD_HIGH_NON_SPECIAL_RANKS.has(context.topCard.rank)) {
    return false;
  }
  if (!context.state.playStack.every((card) => isHighNonSpecialOrResetCard(card))) {
    return false;
  }

  const playableCards = context.playActions.map((action) => getCardForAction(context, action)).filter(Boolean);
  if (playableCards.length === 0) {
    return false;
  }

  return playableCards.every((card) => card.rank === RANK_CLEAR);
}

function isHighNonSpecialOrResetCard(card) {
  return Boolean(card && (card.rank === RANK_RESET || HARD_HIGH_NON_SPECIAL_RANKS.has(card.rank)));
}

function shouldDrawChanceToPreserveStrongCardsHard(context) {
  if (!context.drawAction) {
    return false;
  }
  if (context.deckSize <= HARD_PRESERVE_STRONG_DECK_THRESHOLD) {
    return false;
  }
  if (!context.topCard || getRankValue(context.topCard) > HARD_PRESERVE_STRONG_TOP_RANK_MAX) {
    return false;
  }

  const playableCards = context.playActions.map((action) => getCardForAction(context, action)).filter(Boolean);
  if (playableCards.length === 0) {
    return false;
  }

  if (!playableCards.some((card) => isPowerCard(card))) {
    return false;
  }

  if (!playableCards.some((card) => isHighOrSpecialCard(card))) {
    return false;
  }

  return playableCards.every((card) => isHighOrSpecialCard(card));
}

function shouldDrawChanceToAvoidHighEmptyStackOpenerHard(context) {
  if (!context.drawAction || context.deckSize < HARD_EMPTY_STACK_CHANCE_MIN_DECK_SIZE || context.stackSize !== 0) {
    return false;
  }

  const playableCards = context.playActions.map((action) => getCardForAction(context, action)).filter(Boolean);
  if (playableCards.length === 0) {
    return false;
  }

  return playableCards.every((card) => isPowerCard(card) || HARD_HIGH_NON_SPECIAL_RANKS.has(card.rank));
}

function shouldDrawChanceToAvoidHighRankChainOvercommitHard(context) {
  if (!context.drawAction || !context.topCard) {
    return false;
  }
  const remainingHighDensityPercent = getRemainingHighDensityPercent(context);
  if (remainingHighDensityPercent !== null) {
    if (remainingHighDensityPercent > HARD_OVERCOMMIT_DRAW_MAX_REMAINING_HIGH_DENSITY_PERCENT) {
      return false;
    }
  } else if (context.deckSize < HARD_OVERCOMMIT_DRAW_MIN_DECK_SIZE) {
    return false;
  }
  if (context.handSize > HARD_OVERCOMMIT_DRAW_MAX_HAND_SIZE) {
    return false;
  }
  if (context.opponentHandCount < HARD_OVERCOMMIT_DRAW_MIN_OPPONENT_HAND) {
    return false;
  }
  if (context.stackSize > HARD_OVERCOMMIT_DRAW_MAX_STACK_SIZE) {
    return false;
  }
  if (getRankValue(context.topCard) < HARD_OVERCOMMIT_DRAW_MIN_TOP_RANK) {
    return false;
  }

  const playableCards = context.playActions.map((action) => getCardForAction(context, action)).filter(Boolean);
  if (playableCards.length === 0 || !playableCards.every((card) => isHighOrSpecialCard(card))) {
    return false;
  }

  const matchingTopRankCards = playableCards.filter((card) => card.rank === context.topCard.rank);
  if (matchingTopRankCards.length < HARD_OVERCOMMIT_DRAW_MIN_DUPLICATES) {
    return false;
  }

  if (shouldAllowOvercommitByZoneExitBonusHard(context, matchingTopRankCards.length)) {
    return false;
  }

  const knownOpponentHandCards = getFullyKnownOpponentHandCards(context);
  if (knownOpponentHandCards.length > 0) {
    const opponentCanAnswerTopRank = knownOpponentHandCards.some(
      (card) => isPowerCard(card) || getRankValue(card) >= getRankValue(context.topCard)
    );
    if (!opponentCanAnswerTopRank) {
      return false;
    }
  }

  return true;
}

function getRemainingHighDensityPercent(context) {
  const remainingCards = Array.isArray(context.cpuMemory?.cards?.remainingCards)
    ? context.cpuMemory.cards.remainingCards
    : [];
  if (remainingCards.length === 0) {
    return null;
  }

  let highCardCount = 0;
  for (const card of remainingCards) {
    if (card?.rank && getRankValue(card) >= HARD_ZONE_EXIT_NEXT_ZONE_HIGH_RANK_MIN) {
      highCardCount += 1;
    }
  }

  return (highCardCount / remainingCards.length) * 100;
}

function shouldAllowOvercommitByZoneExitBonusHard(context, matchingRankCount) {
  if (matchingRankCount <= 0) {
    return false;
  }

  const actionZone = getActionZoneName(context.playActions[0], context.player);
  if (!actionZone) {
    return false;
  }

  const zoneCards = getZoneCards(context.player, actionZone);
  if (zoneCards.length === 0 || matchingRankCount > zoneCards.length) {
    return false;
  }

  const remainingTotalCardsAfterDump = getTotalCardCount(context.player) - matchingRankCount;
  if (remainingTotalCardsAfterDump <= 0) {
    return true;
  }
  if (remainingTotalCardsAfterDump <= 1) {
    return true;
  }

  if (zoneCards.length !== matchingRankCount) {
    return false;
  }

  return hasStrongNextZoneAfterExit(context.player, actionZone);
}

function hasStrongNextZoneAfterExit(player, actionZone) {
  if (actionZone !== "hand") {
    return false;
  }

  const nextZoneCards = getZoneCards(player, "faceUp");
  return nextZoneCards.some((card) => isPowerCard(card) || getRankValue(card) >= HARD_ZONE_EXIT_NEXT_ZONE_HIGH_RANK_MIN);
}

function getActionZoneName(action, player) {
  if (!action || !player) {
    return null;
  }

  if (action.zone && Array.isArray(player[action.zone])) {
    return action.zone;
  }

  const cardId = action.cardId;
  if (!cardId) {
    return null;
  }

  if (player.hand.some((card) => card.id === cardId)) {
    return "hand";
  }
  if (player.faceUp.some((card) => card.id === cardId)) {
    return "faceUp";
  }
  if (player.faceDown.some((card) => card.id === cardId)) {
    return "faceDown";
  }

  return null;
}

function getZoneCards(player, zoneName) {
  const cards = player?.[zoneName];
  return Array.isArray(cards) ? cards : [];
}

function getTotalCardCount(player) {
  return getZoneCards(player, "hand").length
    + getZoneCards(player, "faceUp").length
    + getZoneCards(player, "faceDown").length;
}

function chooseKnownHandEliteContinuationActionHard(context) {
  if (context.deckSize !== 0) {
    clearHardEndgameKnownHandConserveTracker(context.cpuMemory);
    return null;
  }

  if (
    !context.skipAction
    || !context.state.nextCardMustBeSameRank
    || !context.topCard
    || isPowerCard(context.topCard)
    || getRankValue(context.topCard) < HARD_ENDGAME_KNOWN_HAND_CONSERVE_RANK_MIN
  ) {
    return null;
  }

  const actionZone = getActionZoneName(context.playActions[0], context.player);
  if (actionZone !== "hand") {
    return null;
  }

  const continuationActions = context.playActions.filter((action) => {
    const card = getCardForAction(context, action);
    return Boolean(card && card.rank === context.topCard.rank);
  });
  if (continuationActions.length === 0 || continuationActions.length !== context.playActions.length) {
    return null;
  }

  if (getTotalCardCount(context.player) - continuationActions.length <= 0) {
    return null;
  }

  const knownOpponentHandCards = getFullyKnownOpponentHandCards(context);
  if (knownOpponentHandCards.length === 0) {
    return null;
  }

  const continuationRankValue = getRankValue(context.topCard);
  for (const card of knownOpponentHandCards) {
    if (isPowerCard(card) || getRankValue(card) > continuationRankValue) {
      return null;
    }
  }

  const loopSignature = [
    context.stackSize,
    context.topCard.rank,
    knownOpponentHandCards.map((card) => card.rank).sort().join(","),
    context.opponentHandCount,
    context.opponent.faceUp.length,
    context.opponent.faceDown.length
  ].join(":");
  const loopCount = incrementHardEndgameKnownHandConserveTracker(context.cpuMemory, loopSignature);
  if (loopCount > HARD_ENDGAME_KNOWN_HAND_CONSERVE_LOOP_LIMIT) {
    return { forceNormalStrategy: true };
  }

  return { action: context.skipAction };
}

function chooseEndgameMemoryPressureActionHard(context, rng) {
  if (!isEndgameMemoryPressureEligible(context)) {
    clearHardEndgameLoopTracker(context.cpuMemory);
    return null;
  }

  const knownOpponentHandCards = getFullyKnownOpponentHandCards(context);
  if (knownOpponentHandCards.length === 0) {
    clearHardEndgameLoopTracker(context.cpuMemory);
    return null;
  }
  if (knownOpponentHandCards.some((card) => isPowerRank(card.rank))) {
    clearHardEndgameLoopTracker(context.cpuMemory);
    return null;
  }

  const highestKnownOpponentRankValue = knownOpponentHandCards.reduce(
    (highest, card) => Math.max(highest, getRankValue(card)),
    0
  );

  const pressureActions = context.playActions.filter((action) => {
    const card = getCardForAction(context, action);
    return Boolean(card && !isPowerCard(card) && getRankValue(card) > highestKnownOpponentRankValue);
  });
  if (pressureActions.length === 0) {
    clearHardEndgameLoopTracker(context.cpuMemory);
    return null;
  }

  const pressureAction = pickLowestRankAction(context, pressureActions, rng);
  const pressureCard = getCardForAction(context, pressureAction);
  if (!pressureCard || !context.topCard) {
    clearHardEndgameLoopTracker(context.cpuMemory);
    return null;
  }

  const pressureLoopSignature = [
    context.stackSize,
    context.topCard.rank,
    pressureCard.rank,
    highestKnownOpponentRankValue,
    context.opponentHandCount
  ].join(":");
  const loopCount = incrementHardEndgameLoopTracker(context.cpuMemory, pressureLoopSignature);
  if (loopCount > HARD_ENDGAME_MEMORY_PRESSURE_LOOP_LIMIT) {
    return { forceNormalStrategy: true };
  }

  return { action: pressureAction };
}

function isEndgameMemoryPressureEligible(context) {
  return Boolean(
    context.deckSize === 0
    && context.playActions.length > 1
    && context.stackSize > 0
    && context.opponentHandCount > 0
  );
}

function chooseEndgameUnknownHandPressureActionHard(context, rng) {
  if (!isEndgameUnknownHandPressureEligible(context)) {
    if (context.deckSize > 0) {
      clearHardEndgameUnknownHandPressureTracker(context.cpuMemory);
    }
    return null;
  }
  if (getKnownOpponentHandCards(context).length > 0) {
    return null;
  }

  const immediateClearAction = context.playActions.find((action) => {
    const card = getCardForAction(context, action);
    return wouldClearByFourOfAKind(context.state, card);
  });
  if (immediateClearAction) {
    clearHardEndgameUnknownHandPressureTracker(context.cpuMemory);
    return null;
  }

  const pressureActions = context.playActions.filter((action) => {
    const card = getCardForAction(context, action);
    return Boolean(card && !isPowerCard(card));
  });
  if (pressureActions.length < HARD_ENDGAME_UNKNOWN_HAND_PRESSURE_MIN_NON_POWER_ACTIONS) {
    clearHardEndgameUnknownHandPressureTracker(context.cpuMemory);
    return null;
  }

  const pressureAction = pickLowestTopSliceRankAction(
    context,
    pressureActions,
    HARD_ENDGAME_UNKNOWN_HAND_PRESSURE_TOP_FRACTION,
    rng
  );
  const pressureCard = getCardForAction(context, pressureAction);
  if (!pressureCard || !context.topCard) {
    clearHardEndgameUnknownHandPressureTracker(context.cpuMemory);
    return null;
  }

  const pressureLoopSignature = [
    context.stackSize,
    context.topCard.rank,
    pressureCard.rank,
    context.opponentHandCount,
    context.opponent.faceDown.length,
    context.state.nextCardMustBeSameRank ? "same" : "free"
  ].join(":");
  const loopCount = incrementHardEndgameUnknownHandPressureTracker(
    context.cpuMemory,
    pressureLoopSignature
  );
  if (loopCount > HARD_ENDGAME_UNKNOWN_HAND_PRESSURE_LOOP_LIMIT) {
    return pickRandom(context.playActions, rng);
  }

  return pressureAction;
}

function isEndgameUnknownHandPressureEligible(context) {
  return Boolean(
    context.deckSize === 0
    && !context.state.nextCardMustBeSameRank
    && context.stackSize > 0
    && context.opponentHandCount > 0
    && context.opponentHandCount <= HARD_ENDGAME_UNKNOWN_HAND_PRESSURE_MAX_OPPONENT_HAND
    && Array.isArray(context.opponent?.faceUp)
    && context.opponent.faceUp.length === 0
    && Array.isArray(context.opponent?.faceDown)
    && context.opponent.faceDown.length === 0
  );
}

function chooseEndgameVisibleLockActionHard(context, rng) {
  if (!isEndgameVisibleLockEligible(context)) {
    if (context.deckSize > 0) {
      clearHardEndgameVisibleLockTracker(context.cpuMemory);
    }
    return null;
  }

  const opponentFaceUpCards = context.opponent.faceUp.filter(Boolean);
  if (opponentFaceUpCards.some((card) => isPowerCard(card))) {
    clearHardEndgameVisibleLockTracker(context.cpuMemory);
    return null;
  }

  const immediateClearAction = context.playActions.find((action) => {
    const card = getCardForAction(context, action);
    return wouldClearByFourOfAKind(context.state, card);
  });
  if (immediateClearAction) {
    clearHardEndgameVisibleLockTracker(context.cpuMemory);
    return null;
  }

  const highestOpponentFaceUpRankValue = opponentFaceUpCards.reduce(
    (highest, card) => Math.max(highest, getRankValue(card)),
    0
  );
  const pressureActions = context.playActions.filter((action) => {
    const card = getCardForAction(context, action);
    return Boolean(card && !isPowerCard(card) && getRankValue(card) > highestOpponentFaceUpRankValue);
  });
  if (pressureActions.length === 0) {
    clearHardEndgameVisibleLockTracker(context.cpuMemory);
    return null;
  }

  const pressureAction = pickLowestRankAction(context, pressureActions, rng);
  const pressureCard = getCardForAction(context, pressureAction);
  if (!pressureCard || !context.topCard) {
    clearHardEndgameVisibleLockTracker(context.cpuMemory);
    return null;
  }

  const pressureLoopSignature = [
    opponentFaceUpCards.map((card) => card.rank).sort().join(","),
    context.opponent.faceDown.length
  ].join(":");
  const loopCount = incrementHardEndgameVisibleLockTracker(context.cpuMemory, pressureLoopSignature);
  if (loopCount > HARD_ENDGAME_VISIBLE_LOCK_LOOP_LIMIT) {
    return { action: pickRandom(context.playActions, rng) };
  }

  return { action: pressureAction };
}

function isEndgameVisibleLockEligible(context) {
  return Boolean(
    context.deckSize === 0
    && !context.state.nextCardMustBeSameRank
    && context.playActions.length > 1
    && context.stackSize > 0
    && context.opponentHandCount === 0
    && Array.isArray(context.opponent?.faceUp)
    && context.opponent.faceUp.length > 0
  );
}

function getFullyKnownOpponentHandCards(context) {
  const knownCards = getKnownOpponentHandCards(context);
  if (knownCards.length === 0 || knownCards.length !== context.opponentHandCount) {
    return [];
  }

  return knownCards;
}

function getKnownOpponentHandCards(context) {
  const knownCards = Array.isArray(context.cpuMemory?.opponent?.zones?.hand?.knownCards)
    ? context.cpuMemory.opponent.zones.hand.knownCards
    : [];

  return knownCards.filter((card) => Boolean(card?.id && card?.suit && card?.rank));
}

function clearHardEndgameLoopTracker(memory) {
  if (!memory) {
    return;
  }

  const meta = ensureCpuMemoryFastMeta(memory);
  meta.hardEndgameLoopSignature = null;
  meta.hardEndgameLoopCount = 0;
}

function incrementHardEndgameLoopTracker(memory, signature) {
  const meta = ensureCpuMemoryFastMeta(memory);
  if (meta.hardEndgameLoopSignature === signature) {
    meta.hardEndgameLoopCount += 1;
  } else {
    meta.hardEndgameLoopSignature = signature;
    meta.hardEndgameLoopCount = 1;
  }

  return meta.hardEndgameLoopCount;
}

function clearHardEndgameVisibleLockTracker(memory) {
  if (!memory) {
    return;
  }

  const meta = ensureCpuMemoryFastMeta(memory);
  meta.hardEndgameVisibleLockSignature = null;
  meta.hardEndgameVisibleLockCount = 0;
}

function incrementHardEndgameVisibleLockTracker(memory, signature) {
  const meta = ensureCpuMemoryFastMeta(memory);
  if (meta.hardEndgameVisibleLockSignature === signature) {
    meta.hardEndgameVisibleLockCount += 1;
  } else {
    meta.hardEndgameVisibleLockSignature = signature;
    meta.hardEndgameVisibleLockCount = 1;
  }

  return meta.hardEndgameVisibleLockCount;
}

function clearHardEndgameUnknownHandPressureTracker(memory) {
  if (!memory) {
    return;
  }

  const meta = ensureCpuMemoryFastMeta(memory);
  meta.hardEndgameUnknownHandPressureSignature = null;
  meta.hardEndgameUnknownHandPressureCount = 0;
}

function incrementHardEndgameUnknownHandPressureTracker(memory, signature) {
  const meta = ensureCpuMemoryFastMeta(memory);
  if (meta.hardEndgameUnknownHandPressureSignature === signature) {
    meta.hardEndgameUnknownHandPressureCount += 1;
  } else {
    meta.hardEndgameUnknownHandPressureSignature = signature;
    meta.hardEndgameUnknownHandPressureCount = 1;
  }

  return meta.hardEndgameUnknownHandPressureCount;
}

function clearHardEndgameKnownHandConserveTracker(memory) {
  if (!memory) {
    return;
  }

  const meta = ensureCpuMemoryFastMeta(memory);
  meta.hardEndgameKnownHandConserveSignature = null;
  meta.hardEndgameKnownHandConserveCount = 0;
}

function incrementHardEndgameKnownHandConserveTracker(memory, signature) {
  const meta = ensureCpuMemoryFastMeta(memory);
  if (meta.hardEndgameKnownHandConserveSignature === signature) {
    meta.hardEndgameKnownHandConserveCount += 1;
  } else {
    meta.hardEndgameKnownHandConserveSignature = signature;
    meta.hardEndgameKnownHandConserveCount = 1;
  }

  return meta.hardEndgameKnownHandConserveCount;
}

function chooseClearOverResetActionHard(context, rng) {
  const stackSizeMin = context.deckSize <= HARD_THIN_DECK_SIZE_MAX
    ? HARD_THIN_DECK_CLEAR_OVER_RESET_STACK_SIZE_MIN
    : HARD_CLEAR_OVER_RESET_STACK_SIZE_MIN;
  if (context.stackSize < stackSizeMin) {
    return null;
  }

  const playableCards = context.playActions.map((action) => getCardForAction(context, action)).filter(Boolean);
  if (playableCards.length === 0 || !playableCards.every((card) => isPowerCard(card))) {
    return null;
  }

  const clearActions = context.playActions.filter((action) => {
    const card = getCardForAction(context, action);
    return Boolean(card && card.rank === RANK_CLEAR);
  });
  if (clearActions.length === 0) {
    return null;
  }

  const hasResetOption = context.playActions.some((action) => {
    const card = getCardForAction(context, action);
    return Boolean(card && card.rank === RANK_RESET);
  });
  if (!hasResetOption) {
    return null;
  }

  return pickLowestRankAction(context, clearActions, rng);
}

function choosePlayCardAction(context, rng) {
  const immediateClearActions = context.playActions.filter((action) => {
    const card = getCardForAction(context, action);
    return wouldClearByFourOfAKind(context.state, card);
  });
  if (immediateClearActions.length > 0) {
    return pickLowestRankAction(context, immediateClearActions, rng);
  }

  const nonPowerActions = context.playActions.filter((action) => {
    const card = getCardForAction(context, action);
    return Boolean(card && !isPowerCard(card));
  });
  if (nonPowerActions.length > 0) {
    return pickLowestRankAction(context, nonPowerActions, rng);
  }

  const nonClearPowerActions = context.playActions.filter((action) => {
    const card = getCardForAction(context, action);
    return Boolean(card && isPowerCard(card) && card.rank !== RANK_CLEAR);
  });
  if (nonClearPowerActions.length > 0) {
    return pickLowestRankAction(context, nonClearPowerActions, rng);
  }

  const clearActions = context.playActions.filter((action) => {
    const card = getCardForAction(context, action);
    return Boolean(card && card.rank === RANK_CLEAR);
  });
  if (clearActions.length > 0) {
    return pickLowestRankAction(context, clearActions, rng);
  }

  return pickRandom(context.playActions, rng);
}

function shouldSkipSameRank(context) {
  const playableCards = context.playActions.map((action) => getCardForAction(context, action)).filter(Boolean);
  if (playableCards.length === 0) {
    return false;
  }

  if (!playableCards.every((card) => isPowerCard(card))) {
    return false;
  }

  const playableCardIds = new Set(playableCards.map((card) => card.id));
  const visibleReserve = [...context.player.hand, ...context.player.faceUp];
  return visibleReserve.some((card) => !isPowerCard(card) && !playableCardIds.has(card.id));
}

function shouldSkipHighRankChainHard(context) {
  if (context.deckSize <= 0) {
    return false;
  }

  const playableCards = context.playActions.map((action) => getCardForAction(context, action)).filter(Boolean);
  if (playableCards.length === 0) {
    return false;
  }

  const chainRankValue = getRankValue(playableCards[0]);
  if (chainRankValue < HARD_SKIP_HIGH_RANK_CHAIN_MIN) {
    return false;
  }

  if (shouldAllowOvercommitByZoneExitBonusHard(context, playableCards.length)) {
    return false;
  }

  return chainRankValue >= HARD_SKIP_HIGH_RANK_CHAIN_MIN;
}

function shouldDrawChanceHard(context) {
  if (!context.drawAction) {
    return false;
  }
  if (!context.pickUpAction) {
    return true;
  }

  if (shouldPickUpSmallHighPileWhileBlockedHard(context)) {
    return false;
  }

  if (context.stackSize >= 3) {
    return true;
  }

  if (context.deckSize <= 4 && context.stackSize <= 2) {
    return false;
  }

  if (context.stackSize <= 1 && context.handSize >= 5) {
    return false;
  }

  return context.topPressure > 0 || context.handSize <= 4;
}

function shouldPickUpSmallHighPileWhileBlockedHard(context) {
  return Boolean(
    context.stackSize >= 1
    && context.stackSize <= HARD_SMALL_HIGH_PILE_PICKUP_MAX_STACK_SIZE
    && context.deckSize >= HARD_SMALL_HIGH_PILE_PICKUP_MIN_DECK_SIZE
    && HARD_HIGH_NON_SPECIAL_RANKS.has(context.topCard?.rank)
    && context.state.playStack.every((card) => card.rank === context.topCard.rank)
  );
}

function wouldClearByFourOfAKind(state, card) {
  if (!card || state.playStack.length < 3) {
    return false;
  }

  const topRank = state.playStack[0].rank;
  if (card.rank !== topRank) {
    return false;
  }

  return state.playStack.slice(0, 3).every((candidate) => candidate.rank === topRank);
}

function getCardForAction(context, action) {
  if (!action || action.type !== "playCard") {
    return null;
  }
  return action[CPU_ACTION_CARD] ?? findActionCard(context.player, action);
}

function findActionCard(player, action) {
  if (!player) {
    return null;
  }
  const actionZoneCards = Array.isArray(player?.[action.zone]) ? player[action.zone] : [];
  return actionZoneCards.find((card) => card.id === action.cardId)
    ?? player.hand.find((card) => card.id === action.cardId)
    ?? player.faceUp.find((card) => card.id === action.cardId)
    ?? null;
}

function isPowerCard(card) {
  return Boolean(card && isPowerRank(card.rank));
}

function isHighOrSpecialCard(card) {
  return Boolean(card && (isPowerCard(card) || getRankValue(card) > HARD_PRESERVE_STRONG_NON_POWER_THRESHOLD));
}

function isPowerRank(rank) {
  return rank === RANK_RESET || rank === RANK_CLEAR;
}

function chooseStrongestSetupFaceUpCards(cards, count) {
  return sortSetupCards(cards).slice(0, count);
}

function chooseHardSetupFaceUpCards(cards, count) {
  const combinations = getCardCombinations(cards, count);
  let best = null;

  for (const combination of combinations) {
    if (!best || isBetterSetupFaceUpSet(combination, best)) {
      best = combination;
    }
  }

  return best ?? [];
}

function isBetterSetupFaceUpSet(candidate, currentBest) {
  const scoreDelta = scoreHardSetupFaceUpSet(candidate) - scoreHardSetupFaceUpSet(currentBest);
  if (scoreDelta !== 0) {
    return scoreDelta > 0;
  }

  return breaksSetupTie(candidate, currentBest);
}

function scoreHardSetupFaceUpSet(cards) {
  return cards.reduce((total, card) => total + getSetupCardStrength(card), 0) + getSetupRankClusterBonus(cards);
}

function getSetupRankClusterBonus(cards) {
  const rankCounts = new Map();
  for (const card of cards) {
    rankCounts.set(card.rank, (rankCounts.get(card.rank) ?? 0) + 1);
  }

  let bonus = 0;
  for (const count of rankCounts.values()) {
    if (count >= 3) {
      bonus += HARD_SETUP_TRIPLE_RANK_BONUS;
    } else if (count === 2) {
      bonus += HARD_SETUP_PAIR_RANK_BONUS;
    }
  }
  return bonus;
}

function breaksSetupTie(candidate, currentBest) {
  const candidateCards = sortSetupCards(candidate);
  const bestCards = sortSetupCards(currentBest);
  const length = Math.min(candidateCards.length, bestCards.length);

  for (let index = 0; index < length; index += 1) {
    const candidateStrength = getSetupCardStrength(candidateCards[index]);
    const bestStrength = getSetupCardStrength(bestCards[index]);
    if (candidateStrength !== bestStrength) {
      return candidateStrength > bestStrength;
    }

    const idDelta = String(candidateCards[index].id).localeCompare(String(bestCards[index].id));
    if (idDelta !== 0) {
      return idDelta < 0;
    }
  }

  return candidateCards.length > bestCards.length;
}

function getCardCombinations(cards, size) {
  if (size <= 0) {
    return [[]];
  }

  const combinations = [];

  function walk(startIndex, selected) {
    if (selected.length === size) {
      combinations.push([...selected]);
      return;
    }

    const remainingNeeded = size - selected.length;
    for (let index = startIndex; index <= cards.length - remainingNeeded; index += 1) {
      selected.push(cards[index]);
      walk(index + 1, selected);
      selected.pop();
    }
  }

  walk(0, []);
  return combinations;
}

function sortSetupCards(cards) {
  return [...cards].sort(compareSetupCardStrength);
}

function compareSetupCardStrength(a, b) {
  const strengthDelta = getSetupCardStrength(b) - getSetupCardStrength(a);
  if (strengthDelta !== 0) {
    return strengthDelta;
  }

  return String(a.id).localeCompare(String(b.id));
}

function getSetupCardStrength(card) {
  if (card.rank === RANK_CLEAR) {
    return 100;
  }
  if (card.rank === RANK_RESET) {
    return 99;
  }
  return getRankValue(card);
}

function pickLowestRankAction(context, actions, rng) {
  let lowestValue = Number.POSITIVE_INFINITY;
  let lowestActions = [];

  for (const action of actions) {
    const card = getCardForAction(context, action);
    if (!card) {
      continue;
    }

    const rankValue = getRankValue(card);
    if (rankValue < lowestValue) {
      lowestValue = rankValue;
      lowestActions = [action];
      continue;
    }
    if (rankValue === lowestValue) {
      lowestActions.push(action);
    }
  }

  return pickRandom(lowestActions.length > 0 ? lowestActions : actions, rng);
}

function pickLowestTopSliceRankAction(context, actions, topFraction, rng) {
  const rankedActions = [];
  for (const action of actions) {
    const card = getCardForAction(context, action);
    if (card) {
      rankedActions.push({ action, card });
    }
  }

  if (rankedActions.length === 0) {
    return null;
  }

  rankedActions.sort((a, b) => {
    const rankDelta = getRankValue(a.card) - getRankValue(b.card);
    if (rankDelta !== 0) {
      return rankDelta;
    }

    return String(a.card.id).localeCompare(String(b.card.id));
  });

  const boundedTopFraction = Math.max(0, Math.min(1, topFraction));
  const topCount = Math.max(1, Math.ceil(rankedActions.length * boundedTopFraction));
  const thresholdIndex = Math.max(0, rankedActions.length - topCount);
  const thresholdRankValue = getRankValue(rankedActions[thresholdIndex].card);
  const thresholdActions = rankedActions
    .filter(({ card }) => getRankValue(card) === thresholdRankValue)
    .map(({ action }) => action);

  return pickRandom(thresholdActions, rng);
}

function pickRandom(items, rng) {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  const randomValue = Math.max(0, Math.min(0.999999, rng()));
  const index = Math.floor(randomValue * items.length);
  return items[index];
}

function annotateHardActionReason(action, reason) {
  if (!action || !reason) {
    return action;
  }

  action.hardReason = reason;
  return action;
}
